import { useState, useEffect } from "react";
import useGet from "../read/useGet";

const useGetChatMessages = (chatId = null, initialLimit = 10, initialPageNo = 1) => {
  const [limit, setLimit] = useState(initialLimit);
  const [pageNo, setPageNo] = useState(initialPageNo);
  const [messages, setMessages] = useState([]);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isInitialCall, setIsInitialCall] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  
  const initialData = {
    limit,
    pageNo
  };
  
  if (chatId) {
    initialData.chat_id = chatId;
  }
  
  const { list, paramsObject, setGetListParams } = useGet({
    url: "chat/single-chat-messages",
    initialData,
    isChat: true,
    enabled: !!chatId,
    onSuccess: (response) => {
      if (response?.success) {
        setTotalCount(response.pagination?.totalCount || 0);
        
        // Using similar pattern to CustomerChat
        if (isInitialCall) {
          // First call - replace existing messages
          setMessages(response.data || []);
          setIsInitialCall(false);
          setIsFirstLoad(false);
        } else {
          // Subsequent pages - prepend new messages (older messages come first)
          console.log("Adding more messages, page:", pageNo);
          setMessages(prevMessages => [...response.data || [], ...prevMessages]);
        }
        setIsFetchingMore(false);
      }
    }
  });

  // Reset pagination when chat changes - only if chatId exists
  useEffect(() => {
    if (chatId) {
      // console.log("Fetching messages for chat ID:", chatId);
      setPageNo(1);
      setIsFirstLoad(true);
      setIsInitialCall(true);
      setMessages([]);
      setGetListParams({
        chat_id: chatId,
        limit,
        pageNo: 1
      });
    }
  }, [chatId]);

  const loadMoreMessages = () => {
    // Only proceed if we have a valid chatId
    if (!chatId || isFetchingMore || !list.data?.pagination.hasMorePages) return;
    
    setIsFetchingMore(true); // set guard flag before loading more
    const nextPage = pageNo + 1;
    // console.log("Loading more messages, page:", nextPage);
    setPageNo(nextPage);
    setGetListParams({
      ...paramsObject,
      chat_id: chatId,
      limit,
      pageNo: nextPage
    });
  };

  // Function to manually trigger a refetch (useful for new chats)
  const refetch = () => {
    if (chatId) {
      list.refetch();
    } else {
      // console.log("Cannot refetch: No valid chat_id available");
    }
  };

  return {
    messages,
    loading: list.isLoading,
    isFetching: list.isFetching, 
    error: list.error,
    pagination: list.data?.pagination,
    loadMoreMessages,
    isFirstLoad,
    hasMoreMessages: list.data?.pagination?.hasMorePages || false,
    refetch
  };
};

export default useGetChatMessages;
